import type { ReactNode } from "react";
import { useLingui } from "@lingui/react/macro";
import { ListOrdered, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Recipe } from "@/types/recipe";
import { multipleLabel, scaledYield, type BatchScale } from "@/lib/recipes/staged-method";
import { ccpStepCount } from "@/lib/recipes/ccp";
import { CcpWarningBadge } from "../ccp";
import { BatchScaleControl } from "./BatchScaleControl";

interface RecipeMethodHeaderProps {
  recipe: Recipe;
  scale: BatchScale;
  onScaleChange: (scale: BatchScale) => void;
  onEditStages?: () => void;
  /** False where printing would pick up the page behind too, as in cook mode. */
  showPrint?: boolean;
}

/** Day-month-year, as the recipe's metadata strip writes it. */
function formatDate(value: string): string {
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}

/**
 * The strip above every recipe card view: what the recipe is, how much it
 * makes, and the batch the quantities below are for.
 */
export function RecipeMethodHeader({
  recipe,
  scale,
  onScaleChange,
  onEditStages,
  showPrint = true,
}: RecipeMethodHeaderProps) {
  const { t } = useLingui();
  const typeLabel = recipe.recipeType === "PLATED" ? t`Plated` : t`Prep`;
  const facts: ReactNode[] = [];
  if (recipe.yieldQuantity) {
    // The yield of the batch being made — "160 × 8 oz" at 5× of a 32.
    const amount = scaledYield(recipe.yieldQuantity, scale) ?? String(recipe.yieldQuantity);
    facts.push(
      <span key="yield">
        {t`Yield`} <b className="text-(--color-text-primary)">{`${amount} ${recipe.yieldUom ?? ""}`.trim()}</b>
      </span>,
    );
  }
  if (scale !== 1) {
    // On paper the card has no toggle to show which batch it is for.
    facts.push(
      <span key="batch">
        {t`Batch`} <b className="text-(--color-text-primary)">{scale === 0.5 ? t`Half` : multipleLabel(scale)}</b>
      </span>,
    );
  }
  if (recipe.updatedAt) {
    facts.push(<span key="revised">{t`Revised ${formatDate(recipe.updatedAt)}`}</span>);
  }
  const ccpCount = ccpStepCount(recipe.instructions);
  if (ccpCount > 0) {
    // A recipe with critical control points says so up here, before the cook reaches a step.
    facts.push(<CcpWarningBadge key="ccp" count={ccpCount} />);
  }

  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      {recipe.imageUrl && (
        // The recipe's photo, for the screen; a printed card keeps to the page's budget of ink.
        <img src={recipe.imageUrl} alt="" className="size-20 shrink-0 rounded-xl object-cover print:hidden" />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-(--color-text-primary) print:text-[16pt]">{recipe.name}</h2>
        <div className="flex flex-wrap items-center gap-2 text-sm text-(--color-text-secondary)">
          <span className="rounded-full bg-(--color-surface-sidebar) px-2.5 py-0.5 text-xs font-semibold text-(--color-text-on-dark)">
            {typeLabel}
          </span>
          {facts.map((fact, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && <span aria-hidden>·</span>}
              {fact}
            </span>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        {onEditStages && (
          <Button type="button" variant="outline" onClick={onEditStages} className="gap-1.5">
            <ListOrdered className="size-4" />
            {t`Edit stages`}
          </Button>
        )}
        <BatchScaleControl
          scale={scale}
          onScaleChange={onScaleChange}
          yieldQuantity={recipe.yieldQuantity}
          yieldUom={recipe.yieldUom}
        />
        {showPrint && (
          <Button type="button" variant="outline" size="icon" onClick={() => window.print()} aria-label={t`Print`}>
            <Printer className="size-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
